// @ts-nocheck
/**
 * LazyLoad Component for Sigma08 Learning Platform
 * Implements intersection observer-based lazy loading for images and content
 */

import React, { useEffect, useRef, useState } from 'react';
import { SkeletonText, SkeletonPlaceholder } from '@carbon/react';

interface LazyLoadProps {
  children: React.ReactNode;
  threshold?: number;
  rootMargin?: string;
  placeholder?: React.ReactNode;
  onLoad?: () => void;
  disabled?: boolean;
  className?: string;
}

interface LazyImageProps {
  src: string;
  alt: string;
  placeholder?: string;
  className?: string;
  width?: number | string;
  height?: number | string;
  srcSet?: string;
  sizes?: string;
  onLoad?: () => void;
  onError?: () => void;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'sync' | 'auto';
  fetchPriority?: 'high' | 'low' | 'auto';
}

interface LazyVideoProps {
  src: string;
  poster?: string;
  className?: string;
  width?: number | string;
  height?: number | string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  onLoad?: () => void;
}

// Global IntersectionObserver instance for better performance
let globalObserver: IntersectionObserver | null = null;
const observerCallbacks = new WeakMap<Element, () => void>();

function getGlobalObserver(options: IntersectionObserverInit = {}) {
  if (!globalObserver && typeof window !== 'undefined' && 'IntersectionObserver' in window) {
    globalObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const callback = observerCallbacks.get(entry.target);
            if (callback) {
              callback();
              globalObserver?.unobserve(entry.target);
              observerCallbacks.delete(entry.target);
            }
          }
        });
      },
      {
        threshold: options.threshold || 0.1,
        rootMargin: options.rootMargin || '50px',
        ...options,
      }
    );
  }
  return globalObserver;
}

/**
 * LazyLoad component for general content
 */
export const LazyLoad: React.FC<LazyLoadProps> = ({
  children,
  threshold = 0.1,
  rootMargin = '50px',
  placeholder = <SkeletonText />,
  onLoad,
  disabled = false,
  className = '',
}) => {
  const [isLoaded, setIsLoaded] = useState(disabled);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled || isLoaded) return;

    const element = elementRef.current;
    if (!element) return;

    const observer = getGlobalObserver({ threshold, rootMargin });
    if (!observer) {
      // Fallback for browsers without IntersectionObserver
      setIsLoaded(true);
      onLoad?.();
      return;
    }

    const handleIntersection = () => {
      setIsLoaded(true);
      onLoad?.();
    };

    observerCallbacks.set(element, handleIntersection);
    observer.observe(element);

    return () => {
      if (element && observer) {
        observer.unobserve(element);
        observerCallbacks.delete(element);
      }
    };
  }, [disabled, isLoaded, threshold, rootMargin, onLoad]);

  return (
    <div ref={elementRef} className={className}>
      {isLoaded ? children : placeholder}
    </div>
  );
};

/**
 * LazyImage component with progressive loading
 */
export const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  placeholder = '/images/placeholder.svg',
  className = '',
  width,
  height,
  srcSet,
  sizes,
  onLoad,
  onError,
  loading = 'lazy',
  decoding = 'async',
  fetchPriority = 'auto',
}) => {
  const [imageSrc, setImageSrc] = useState<string>(placeholder);
  const [imageState, setImageState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const imgRef = useRef<HTMLImageElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    // Use native lazy loading if supported
    if ('loading' in HTMLImageElement.prototype && loading === 'lazy') {
      setImageSrc(src);
      return;
    }

    // Fall back to IntersectionObserver
    const observer = getGlobalObserver();
    if (!observer) {
      setImageSrc(src);
      return;
    }

    const handleIntersection = () => {
      setIsInView(true);
    };

    observerCallbacks.set(img, handleIntersection);
    observer.observe(img);

    return () => {
      if (img && observer) {
        observer.unobserve(img);
        observerCallbacks.delete(img);
      }
    };
  }, [src, loading]);

  useEffect(() => {
    if (!isInView && loading === 'lazy') return;

    // Preload image
    const img = new Image();

    img.onload = () => {
      setImageSrc(src);
      setImageState('loaded');
      onLoad?.();

      // Trigger reflow optimization
      if (imgRef.current) {
        imgRef.current.classList.add('lazy-loaded');
      }
    };

    img.onerror = () => {
      setImageState('error');
      onError?.();
    };

    // Set srcset and sizes if provided
    if (srcSet) img.srcset = srcSet;
    if (sizes) img.sizes = sizes;

    img.src = src;
  }, [isInView, src, srcSet, sizes, onLoad, onError, loading]);

  return (
    <img
      ref={imgRef}
      src={imageSrc}
      alt={alt}
      className={`lazy-image ${className} ${imageState}`}
      width={width}
      height={height}
      srcSet={imageState === 'loaded' ? srcSet : undefined}
      sizes={imageState === 'loaded' ? sizes : undefined}
      loading={loading}
      decoding={decoding}
      fetchPriority={fetchPriority}
      style={{
        opacity: imageState === 'loaded' ? 1 : 0,
        transition: 'opacity 0.3s ease-in-out',
      }}
    />
  );
};

/**
 * LazyVideo component for video content
 */
export const LazyVideo: React.FC<LazyVideoProps> = ({
  src,
  poster,
  className = '',
  width,
  height,
  autoPlay = false,
  muted = true,
  loop = false,
  controls = true,
  onLoad,
}) => {
  const [videoSrc, setVideoSrc] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = getGlobalObserver();
    if (!observer) {
      setVideoSrc(src);
      return;
    }

    const handleIntersection = () => {
      setVideoSrc(src);
      setIsLoaded(true);
      onLoad?.();
    };

    observerCallbacks.set(video, handleIntersection);
    observer.observe(video);

    return () => {
      if (video && observer) {
        observer.unobserve(video);
        observerCallbacks.delete(video);
      }
    };
  }, [src, onLoad]);

  return (
    <video
      ref={videoRef}
      className={`lazy-video ${className}`}
      width={width}
      height={height}
      poster={poster}
      autoPlay={autoPlay && isLoaded}
      muted={muted}
      loop={loop}
      controls={controls}
      preload="none"
      style={{
        opacity: isLoaded ? 1 : 0,
        transition: 'opacity 0.3s ease-in-out',
      }}
    >
      {videoSrc && <source src={videoSrc} type="video/mp4" />}
    </video>
  );
};

/**
 * LazyIframe component for embedded content
 */
interface LazyIframeProps {
  src: string;
  title: string;
  className?: string;
  width?: number | string;
  height?: number | string;
  sandbox?: string;
  allow?: string;
  onLoad?: () => void;
}

export const LazyIframe: React.FC<LazyIframeProps> = ({
  src,
  title,
  className = '',
  width = '100%',
  height = 400,
  sandbox,
  allow,
  onLoad,
}) => {
  const [iframeSrc, setIframeSrc] = useState<string>('');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const observer = getGlobalObserver({ rootMargin: '100px' });
    if (!observer) {
      setIframeSrc(src);
      return;
    }

    const handleIntersection = () => {
      setIframeSrc(src);
      onLoad?.();
    };

    observerCallbacks.set(iframe, handleIntersection);
    observer.observe(iframe);

    return () => {
      if (iframe && observer) {
        observer.unobserve(iframe);
        observerCallbacks.delete(iframe);
      }
    };
  }, [src, onLoad]);

  return (
    <iframe
      ref={iframeRef}
      src={iframeSrc}
      title={title}
      className={`lazy-iframe ${className}`}
      width={width}
      height={height}
      sandbox={sandbox}
      allow={allow}
      loading="lazy"
      style={{
        border: 'none',
        opacity: iframeSrc ? 1 : 0,
        transition: 'opacity 0.3s ease-in-out',
      }}
    />
  );
};

/**
 * LazySection component for large content sections
 */
interface LazySectionProps {
  children: React.ReactNode;
  skeleton?: React.ReactNode;
  minHeight?: number | string;
  className?: string;
  onLoad?: () => void;
}

export const LazySection: React.FC<LazySectionProps> = ({
  children,
  skeleton,
  minHeight = 200,
  className = '',
  onLoad,
}) => {
  const defaultSkeleton = (
    <div style={{ minHeight }}>
      <SkeletonPlaceholder />
    </div>
  );

  return (
    <LazyLoad
      className={`lazy-section ${className}`}
      placeholder={skeleton || defaultSkeleton}
      onLoad={onLoad}
      threshold={0.01}
      rootMargin="200px"
    >
      {children}
    </LazyLoad>
  );
};

/**
 * Preload critical images
 */
export const preloadImages = (urls: string[]) => {
  urls.forEach(url => {
    const img = new Image();
    img.src = url;
  });
};

/**
 * Preconnect to external domains
 */
export const preconnectDomains = (domains: string[]) => {
  domains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = domain;
    document.head.appendChild(link);
  });
};

// Export utility to clean up observer
export const cleanupLazyLoadObserver = () => {
  if (globalObserver) {
    globalObserver.disconnect();
    globalObserver = null;
  }
  observerCallbacks.clear();
};

export default LazyLoad;
